/**
 * Session state in localStorage: the token (`JWT <token>` format), the user
 * profile and the logout behaviour.
 */
import { ROLES, STORAGE_KEYS, type Role } from '@/shared/constants';
import { local, session as sessionStorage_ } from '@/shared/lib/storage';
import { hardNavigate } from '@/shared/lib/navigation';
import { isTokenExpired } from './jwt';

export interface UserProfile {
  id: string;
  username: string;
  email: string;
  language: string;
  role: Role | string;
  disabled?: boolean;
  abcdiorAccess?: boolean;
  dmsAccess?: boolean;
  assignedStores?: string[];
  workshopIds?: string[];
  features?: Record<string, boolean>;
  [extra: string]: unknown;
}

export const getToken = (): string | null => local.get(STORAGE_KEYS.TOKEN);

export const storeUserData = (
  token: string,
  user: Partial<UserProfile> | Record<string, never>,
): void => {
  local.set(STORAGE_KEYS.TOKEN, token);
  local.setJson(STORAGE_KEYS.USER, user);
};

export const isLoggedIn = (): boolean => !isTokenExpired(getToken());

/**
 * Clears the whole session and sends the user to the login page.
 * `errorCode` is picked up by the login page to display a translated message
 * (`login.<code>`).
 */
export const logout = (errorCode?: string): void => {
  local.clear();
  if (errorCode) {
    sessionStorage_.set(STORAGE_KEYS.LOGIN_ERROR_CODE, errorCode);
  }
  hardNavigate('/login');
};

/**
 * Returns the stored profile, or null when none is stored.
 * Sales associates have no access to the back office: reading their profile
 * logs them out.
 */
export const getUserProfile = (): UserProfile | null => {
  const user = local.getJson<UserProfile>(STORAGE_KEYS.USER);
  if (!user) return null;
  if (user.role === ROLES.SALES_ASSOCIATE) {
    logout();
    return null;
  }
  return user;
};

/** Valid token and account not disabled. */
export const canActivate = (): boolean => {
  if (!isLoggedIn()) return false;
  const user = local.getJson<UserProfile>(STORAGE_KEYS.USER);
  return !!user && !user.disabled && user.role !== ROLES.SALES_ASSOCIATE;
};

export const consumeLoginErrorCode = (): string | null => {
  const code = sessionStorage_.get(STORAGE_KEYS.LOGIN_ERROR_CODE);
  sessionStorage_.remove(STORAGE_KEYS.LOGIN_ERROR_CODE);
  return code;
};

export const setLoginErrorCode = (code: string): void => {
  sessionStorage_.set(STORAGE_KEYS.LOGIN_ERROR_CODE, code);
};
